/**
 * CRM Sidebar — left navigation for the CRM shell.
 *
 * Desktop (lg+): a sticky column below the h-12 app bar with a footer holding
 * Help + the collapse toggle (w-60 labels <-> w-16 icon rail; persisted via
 * getPref/setPref('sidebar')). When the user has never chosen, the rail is the
 * default on laptop-width screens (<2xl) so content gets the width.
 * Mobile (<lg): hidden until `mobileOpen`, then a fixed slide-over drawer with
 * full labels; closes on overlay click, Escape, or any nav click.
 *
 * IA: the daily-driver views render first (Today/Calendar, then the Leads and
 * Comms groups, then Reports), and every config surface lives behind the single
 * Settings item (the /crm/settings hub carries its own sub-nav — see
 * SettingsLayout). The shell remounts per route, so the collapse state comes
 * from localStorage on each mount.
 *
 * Active/No-reply badges come from GET /api/crm/contacts/stats
 * (stats.conversations), refreshed every 60s and immediately on the
 * STATS_CHANGED_EVENT window event (pages dispatch it after state-changing
 * PATCHes). A failed fetch just hides the badges — navigation must never
 * break because a count endpoint hiccuped.
 *
 * The Active conversations badge uses the needs_reply count (leads who
 * replied and still owe us a response), not the full active count — it
 * mirrors the tab's default "Awaiting my reply" filter, so the badge is the
 * actionable backlog rather than every conversation that ever got a reply.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { NavLink } from 'react-router-dom';
import {
  BarChart3,
  BookOpen,
  CalendarDays,
  ChevronsLeft,
  ChevronsRight,
  Database,
  Hourglass,
  Inbox,
  MessagesSquare,
  Microscope,
  Phone,
  Printer,
  Settings,
  Sun,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import api from '../../services/api';
import { LifecycleStats } from '../types';
import { getPref, setPref, STATS_CHANGED_EVENT } from '../lib';
import CrmMark from './CrmMark';

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

interface NavItem {
  to: string;
  end?: boolean;
  icon: LucideIcon;
  label: string;
  /** Badge count (undefined/null/0 -> no badge). */
  badge?: number | null;
}

interface ConversationCounts {
  /** Leads who replied and still owe us a response — drives the Active badge. */
  needs_reply: number;
  awaiting: number;
}

function NavRow({
  item,
  collapsed,
  inDrawer,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  /** Rendered inside the mobile drawer (exists up to lg) — keep 44px rows until lg. */
  inDrawer?: boolean;
  onNavigate?: () => void;
}) {
  const { to, end, icon: Icon, label, badge } = item;
  const showBadge = typeof badge === 'number' && badge > 0;
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      title={collapsed ? label : undefined}
      aria-label={showBadge ? `${label} (${badge})` : label}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg text-sm font-medium transition-colors duration-150 ${
          inDrawer ? 'min-h-[44px] lg:min-h-0 lg:py-1.5' : 'min-h-[44px] sm:min-h-0 sm:py-1.5'
        } focus:outline-none focus:ring-2 focus:ring-primary-500 ${
          collapsed ? 'justify-center px-2' : 'px-3'
        } ${
          isActive
            ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700/60 dark:hover:text-gray-100'
        }`
      }
    >
      <span className="relative flex-shrink-0">
        <Icon className="h-4 w-4" />
        {/* Collapsed mode: the count becomes a corner dot on the icon */}
        {collapsed && showBadge && (
          <span
            className="absolute -right-1.5 -top-1.5 h-2 w-2 rounded-full bg-primary-600 ring-2 ring-white dark:ring-gray-800"
            aria-hidden="true"
          />
        )}
      </span>
      {!collapsed && <span className="min-w-0 flex-1 truncate">{label}</span>}
      {!collapsed && showBadge && (
        <span className="ml-auto flex-shrink-0 rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
          {badge! > 99 ? '99+' : badge}
        </span>
      )}
    </NavLink>
  );
}

export default function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  // Explicit user choice wins; otherwise default to the icon rail on
  // laptop-width screens (below 2xl) and to full labels on large desktops.
  const [collapsed, setCollapsed] = useState(() => {
    const stored = getPref('sidebar', '');
    if (stored === 'collapsed') return true;
    if (stored === 'expanded') return false;
    try {
      return !window.matchMedia('(min-width: 1536px)').matches;
    } catch {
      return false;
    }
  });
  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      setPref('sidebar', next ? 'collapsed' : 'expanded');
      return next;
    });
  }, []);

  // Keep the width-based default live across window resizes — but only until
  // the user makes an explicit choice.
  useEffect(() => {
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia('(min-width: 1536px)');
    } catch {
      return;
    }
    const apply = () => {
      if (getPref('sidebar', '') === '') setCollapsed(!mq.matches);
    };
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    }
    return undefined;
  }, []);

  // Conversation counts for the Active / No-reply badges, plus the Today badge
  // (counts-only worklist — total actionable items for the signed-in user).
  // Silent failure: a null state simply renders no badges. Pages dispatch
  // STATS_CHANGED_EVENT after state-changing PATCHes; the 60s interval is the
  // fallback.
  const [counts, setCounts] = useState<ConversationCounts | null>(null);
  const [todayCount, setTodayCount] = useState<number | null>(null);
  const [faxCount, setFaxCount] = useState<number | null>(null);
  const [vmCount, setVmCount] = useState<number | null>(null);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.get('/api/crm/contacts/stats');
        const stats = res.data as LifecycleStats | null;
        const conv = stats?.conversations;
        if (alive) {
          setCounts(conv ? { needs_reply: conv.needs_reply, awaiting: conv.awaiting } : null);
          setFaxCount(typeof stats?.fax_needs_match === 'number' ? stats.fax_needs_match : null);
          setVmCount(typeof stats?.voicemail_unheard === 'number' ? stats.voicemail_unheard : null);
        }
      } catch {
        if (alive) {
          setCounts(null);
          setFaxCount(null);
          setVmCount(null);
        }
      }
      try {
        const res = await api.get('/api/crm/worklist', { params: { counts: 'true' } });
        if (alive) setTodayCount(typeof res.data?.total_actionable === 'number' ? res.data.total_actionable : null);
      } catch {
        if (alive) setTodayCount(null);
      }
    };
    load();
    const timer = window.setInterval(load, 60_000);
    window.addEventListener(STATS_CHANGED_EVENT, load);
    return () => {
      alive = false;
      window.clearInterval(timer);
      window.removeEventListener(STATS_CHANGED_EVENT, load);
    };
  }, []);

  // Close the mobile drawer on Escape while it is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onMobileClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen, onMobileClose]);

  // Drawer focus management: on open, lock body scroll and move focus to the
  // close button; on close (or unmount while open), restore both the previous
  // body overflow and focus to whatever element opened the drawer.
  const panelRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!mobileOpen) return;
    const prevFocus = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeBtnRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      prevFocus?.focus();
    };
  }, [mobileOpen]);

  // Minimal Tab loop-trap: keep Tab / Shift+Tab cycling inside the drawer panel.
  const onDrawerKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusables = panel.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || !panel.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last || !panel.contains(active)) {
      e.preventDefault();
      first.focus();
    }
  };

  // Everyday views, grouped. A header renders as a muted label when expanded;
  // section boundaries are thin dividers in both modes.
  const sections: { header?: string; items: NavItem[] }[] = [
    {
      items: [
        { to: '/crm', end: true, icon: Sun, label: 'Today', badge: todayCount },
        { to: '/crm/calendar', icon: CalendarDays, label: 'Calendar' },
      ],
    },
    {
      header: 'Leads',
      items: [
        { to: '/crm/inbox', icon: Inbox, label: 'Lead Inbox' },
        { to: '/crm/active', icon: MessagesSquare, label: 'Active conversations', badge: counts?.needs_reply },
        { to: '/crm/awaiting', icon: Hourglass, label: 'No reply yet', badge: counts?.awaiting },
        { to: '/crm/by-study', icon: Microscope, label: 'By study' },
        { to: '/crm/database', icon: Database, label: 'Patient database' },
      ],
    },
    {
      header: 'Comms',
      items: [
        { to: '/crm/phone', icon: Phone, label: 'Phone', badge: vmCount },
        // Fax hub: the unmatched-triage queue, history search, standalone send,
        // and the provider-office directory. Open to all CRM users (recruiters
        // work the fax queue); the badge is the unmatched-inbound count.
        { to: '/crm/fax', icon: Printer, label: 'Fax', badge: faxCount },
      ],
    },
    {
      items: [{ to: '/crm/reports', icon: BarChart3, label: 'Reports' }],
    },
    // One entry for every config surface — the /crm/settings hub carries its
    // own role-gated sub-nav (studies/forms/flows are open to all CRM users,
    // so the item itself is unconditional).
    {
      items: [{ to: '/crm/settings', icon: Settings, label: 'Settings' }],
    },
  ];

  // In-app handbooks — visible to ALL CRM users (the page itself only uses
  // the admin role to pick a default tab; both handbooks are readable by all).
  const helpItem: NavItem = { to: '/crm/help', icon: BookOpen, label: 'Help' };

  const dividerCls = 'mt-3 border-t border-gray-100 pt-3 dark:border-gray-800';
  const groupLabelCls =
    'mb-1 px-3 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500';

  const renderNav = (collapsedMode: boolean, inDrawer?: boolean, onNavigate?: () => void) => (
    <>
      {sections.map((section, idx) => (
        <div key={section.header || `s${idx}`} className={idx > 0 ? dividerCls : undefined}>
          {section.header && !collapsedMode && <div className={groupLabelCls}>{section.header}</div>}
          <div className="space-y-0.5">
            {section.items.map((item) => (
              <NavRow key={item.to} item={item} collapsed={collapsedMode} inDrawer={inDrawer} onNavigate={onNavigate} />
            ))}
          </div>
        </div>
      ))}
    </>
  );

  return (
    <>
      {/* ---- Desktop: sticky column below the h-12 app bar, collapsible ---- */}
      <aside
        className={`hidden flex-shrink-0 self-stretch border-r border-gray-200 bg-white transition-[width] duration-150 dark:border-gray-700 dark:bg-gray-800 lg:flex lg:flex-col ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        <div className="sticky top-12 flex h-[calc(100vh-3rem)] w-full flex-col">
          <nav aria-label="CRM navigation" className="flex-1 overflow-y-auto p-3">
            {renderNav(collapsed)}
          </nav>
          <div
            className={`border-t border-gray-200 p-2 dark:border-gray-700 ${
              collapsed ? 'space-y-0.5' : 'flex items-center gap-1'
            }`}
          >
            {collapsed ? (
              <>
                <NavRow item={helpItem} collapsed />
                <button
                  type="button"
                  onClick={toggleCollapsed}
                  aria-label="Expand sidebar"
                  title="Expand sidebar"
                  className="flex w-full items-center justify-center rounded-lg px-2 py-1.5 text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700/60 dark:hover:text-gray-200"
                >
                  <ChevronsRight className="h-4 w-4 flex-shrink-0" />
                </button>
              </>
            ) : (
              <>
                <div className="min-w-0 flex-1">
                  <NavRow item={helpItem} collapsed={false} />
                </div>
                <button
                  type="button"
                  onClick={toggleCollapsed}
                  aria-label="Collapse sidebar"
                  title="Collapse sidebar"
                  className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700/60 dark:hover:text-gray-200"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </aside>

      {/* ---- Mobile: slide-over drawer ---- */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={onMobileClose} aria-hidden="true" />
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="CRM navigation"
            onKeyDown={onDrawerKeyDown}
            className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-white shadow-xl dark:bg-gray-800"
          >
            <div className="flex items-center justify-between border-b border-gray-200 py-2 pl-4 pr-2 dark:border-gray-700">
              <span className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                <span className="flex h-5 w-5 items-center justify-center rounded bg-primary-600">
                  <CrmMark className="h-3.5 w-3.5 text-white" />
                </span>
                Lead CRM
              </span>
              <button
                ref={closeBtnRef}
                type="button"
                onClick={onMobileClose}
                aria-label="Close navigation"
                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700/60 dark:hover:text-gray-200 sm:min-h-0 sm:min-w-0 sm:p-2"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <nav aria-label="CRM navigation" className="flex-1 overflow-y-auto p-3">
              {renderNav(false, true, onMobileClose)}
              <div className={dividerCls}>
                <NavRow item={helpItem} collapsed={false} inDrawer onNavigate={onMobileClose} />
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}
